import type { IVariable, TextVariable } from '@carboncopyco/types';
import { useCallback, useMemo } from 'react';
import { object, string } from 'yup';
import { ValidationErrorMessages } from '../constants';
import {
  getVariableDescriptionSchema,
  getVariableNameSchema,
} from '../schemas';

interface FormSchemaOptions {
  variable?: TextVariable;
  variables?: IVariable[];
}

export const useFormSchema = ({
  variable,
  variables = [],
}: FormSchemaOptions) => {
  const getExistingValues = useCallback(
    (key: keyof TextVariable) =>
      variables
        .filter((vari) => vari[key] !== variable?.[key])
        .map((vari) => vari[key]),
    [variable, variables]
  );

  const existingNames = useMemo(
    () => getExistingValues('name'),
    [getExistingValues]
  );

  const existingCodes = useMemo(
    () => getExistingValues('code'),
    [getExistingValues]
  );

  return useMemo(() => {
    const lengthLimit = 200;
    const limitErrorMessage = ValidationErrorMessages.contain200;

    return object({
      name: getVariableNameSchema({ existingNames }),
      code: string()
        .matches(/^[a-zA-Z0-9_]+$/, ValidationErrorMessages.containSimpleChars)
        .matches(/^[a-zA-Z]/, ValidationErrorMessages.startWithLetter)
        .notOneOf(
          existingCodes,
          ValidationErrorMessages.variableCodeAlreadyExists
        )
        .required(ValidationErrorMessages.required)
        .max(40, ValidationErrorMessages.contain40),
      value: string().max(lengthLimit, limitErrorMessage),
      description: getVariableDescriptionSchema(),
    });
  }, [existingNames, existingCodes]);
};
